import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Alert,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, Grid, Tooltip, IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Translate as TranslateIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import api from '../../services/api';

const OFFER_TYPES = ['equipment', 'service', 'ride_share', 'property', 'vehicle', 'product'];
const FIELD_TYPES = ['text', 'number', 'boolean', 'select'];
const SEARCH_WIDGETS = ['range', 'toggle', 'select', 'checkbox'];
const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' }
];

const emptyForm = {
  offer_type: 'location',
  key: '',
  label: '',
  field_type: 'text',
  unit: '',
  options: '',
  is_searchable: false,
  search_widget: '',
  display_order: 0
};

export default function AdminListingFieldsPage() {
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [fieldsByOffer, setFieldsByOffer] = useState({});
  const [selectedOffer, setSelectedOffer] = useState('location');
  const [translations, setTranslations] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editTransDialog, setEditTransDialog] = useState({ open: false, row: null });
  const [editTransValue, setEditTransValue] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const loadFields = useCallback(async () => {
    try {
      setLoadError(null);
      const promises = OFFER_TYPES.map(ot =>
        api.get('/listing-field-definitions', { params: { offer_type: ot } })
          .then(r => {
            const data = r.data?.data || r.data || [];
            return { ot, data: Array.isArray(data) ? data : [] };
          })
          .catch(err => {
            console.warn(`Failed to load ${ot} fields:`, err.message);
            return { ot, data: [] };
          })
      );
      const results = await Promise.all(promises);
      const map = {};
      results.forEach(({ ot, data }) => { map[ot] = data; });
      setFieldsByOffer(map);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
      console.error('loadFields error:', err);
      setLoadError(`Erreur chargement champs: ${msg}`);
    }
  }, []);

  const loadTranslations = useCallback(async () => {
    try {
      const res = await api.get('/admin/listing-field-translations');
      const data = res.data?.data || res.data || [];
      setTranslations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('loadTranslations error:', err);
      setTranslations([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadFields();
      await loadTranslations();
      setLoading(false);
    };
    init();
  }, [loadFields, loadTranslations]);

  const handleAutoTranslate = async () => {
    setAutoTranslating(true);
    try {
      const res = await api.post('/admin/listing-field-translations/auto-translate');
      const { stats, success } = res.data || {};
      if (!success) throw new Error('Erreur serveur');
      showNotification({
        type: 'success',
        title: 'Traduction terminée',
        message: stats
          ? `${stats.fieldsProcessed || stats.created || 0} champs, ${stats.unitsTranslated || 0} traductions.`
          : 'Traduction réussie.'
      });
      await loadTranslations();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
      console.error('autoTranslate error:', err);
      showNotification({
        type: 'error',
        title: 'Erreur DeepL/API',
        message: msg
      });
    } finally {
      setAutoTranslating(false);
    }
  };

  const handleCreateField = async () => {
    const { offer_type, key, label, field_type } = form;
    if (!offer_type || !key.trim() || !label.trim() || !field_type) {
      showNotification({
        type: 'warning',
        title: 'Validation',
        message: 'Type d\'offre, clé, label (FR) et type de champ sont obligatoires'
      });
      return;
    }
    if (!/^[a-z0-9_]+$/.test(key)) {
      showNotification({
        type: 'warning',
        title: 'Validation',
        message: 'La clé doit être minuscules, chiffres, underscores (ex: nb_chambres)'
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        offer_type,
        key: key.trim(),
        label: label.trim(),
        field_type,
        unit: form.unit.trim() || null,
        options: form.options
          ? form.options.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        is_searchable: form.is_searchable,
        search_widget: form.search_widget || null,
        display_order: Number(form.display_order) || 0,
        default_value: null
      };

      const res = await api.post('/admin/listing-field-translations/field-definitions', payload);
      const { stats, success } = res.data || {};
      if (!success) throw new Error('Réponse serveur invalide');

      showNotification({
        type: 'success',
        title: 'Champ créé',
        message: stats
          ? `Créé + ${stats.created || 0} traductions générées via DeepL.`
          : 'Champ créé avec succès.'
      });
      setCreateDialog(false);
      setForm(emptyForm);
      await loadFields();
      await loadTranslations();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
      console.error('createField error:', err);
      showNotification({
        type: 'error',
        title: 'Erreur création',
        message: msg
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveTranslation = async () => {
    const { row } = editTransDialog;
    if (!row || !editTransValue.trim()) return;
    try {
      const res = await api.put('/admin/listing-field-translations', {
        type: row.type,
        field_key: row.field_key,
        option_value: row.type === 'option' ? row.option_value : null,
        language: row.language,
        translation: editTransValue.trim()
      });
      if (!res.data?.success) throw new Error('Réponse invalide');
      showNotification({ type: 'success', title: 'Succès', message: 'Traduction mise à jour' });
      setEditTransDialog({ open: false, row: null });
      await loadTranslations();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
      console.error('saveTranslation error:', err);
      showNotification({ type: 'error', title: 'Erreur', message: msg });
    }
  };

  const currentFields = fieldsByOffer[selectedOffer] || [];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Chargement des définitions et traductions...</Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>📋 Champs de détails des annonces</Typography>
          <Typography variant="body2" color="text.secondary">
            Créez et gérez les champs structurés par type d'offre avec traductions automatiques DeepL.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Tooltip title="Recharger les données">
            <Button
              variant="outlined" size="small"
              startIcon={<RefreshIcon />}
              onClick={() => { loadFields(); loadTranslations(); }}
            >
              Rafraîchir
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={autoTranslating ? <CircularProgress size={16} /> : <TranslateIcon />}
            onClick={handleAutoTranslate}
            disabled={autoTranslating}
          >
            Auto-traduire tout
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Nouveau champ
          </Button>
        </Box>
      </Box>

      {loadError && (
        <Alert severity="error" onClose={() => setLoadError(null)} sx={{ mb: 2 }}>
          <strong>Erreur:</strong> {loadError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {OFFER_TYPES.map(ot => {
          const count = (fieldsByOffer[ot] || []).length;
          return (
            <Chip
              key={ot}
              label={`${ot} (${count})`}
              onClick={() => setSelectedOffer(ot)}
              color={selectedOffer === ot ? 'primary' : 'default'}
              variant={selectedOffer === ot ? 'filled' : 'outlined'}
              icon={count === 0 ? <WarningIcon /> : undefined}
            />
          );
        })}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Définitions — <strong>{selectedOffer}</strong>
          </Typography>
          {currentFields.length === 0 ? (
            <Alert severity="info">Aucun champ défini. Cliquez sur "Nouveau champ" pour en créer.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Clé</strong></TableCell>
                    <TableCell><strong>Label (FR)</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Unité</strong></TableCell>
                    <TableCell><strong>Options</strong></TableCell>
                    <TableCell><strong>Recherche</strong></TableCell>
                    <TableCell><strong>Traductions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentFields
                    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                    .map(def => {
                      const langs = LANGUAGES.map(l => l.code);
                      const covered = langs.filter(lng =>
                        translations.some(tr => tr.field_key === def.key && tr.type === 'label' && tr.language === lng)
                      );
                      const opts = Array.isArray(def.options) ? def.options : JSON.parse(def.options || '[]');
                      return (
                        <TableRow key={def.id} hover>
                          <TableCell><code style={{ fontSize: '0.85em' }}>{def.key}</code></TableCell>
                          <TableCell>{def.label}</TableCell>
                          <TableCell><Chip label={def.field_type} size="small" /></TableCell>
                          <TableCell>{def.unit || '—'}</TableCell>
                          <TableCell>
                            {opts.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                                {opts.slice(0, 3).map(o => (
                                  <Chip key={o} label={o} size="small" variant="outlined" />
                                ))}
                                {opts.length > 3 && <Chip label={`+${opts.length - 3}`} size="small" />}
                              </Box>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            {def.is_searchable ? (
                              <Chip label={def.search_widget || 'auto'} size="small" color="success" variant="outlined" />
                            ) : (
                              <span style={{ color: '#999' }}>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${covered.length}/${langs.length}`}
                              size="small"
                              color={covered.length === langs.length ? 'success' : covered.length > 0 ? 'warning' : 'error'}
                              variant={covered.length === langs.length ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Traductions — <strong>{selectedOffer}</strong>
          </Typography>
          {translations.length === 0 ? (
            <Alert severity="warning" icon={<WarningIcon />}>
              Aucune traduction enregistrée. Cliquez sur <strong>Auto-traduire tout</strong> pour générer les traductions DeepL.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Clé</strong></TableCell>
                    <TableCell><strong>Valeur</strong></TableCell>
                    <TableCell><strong>Langue</strong></TableCell>
                    <TableCell><strong>Traduction</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {translations
                    .filter(tr => currentFields.some(f => f.key === tr.field_key))
                    .slice(0, 50)
                    .map(tr => (
                      <TableRow key={tr.id} hover>
                        <TableCell>
                          <Chip
                            label={tr.type}
                            size="small"
                            color={tr.type === 'label' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell><code style={{ fontSize: '0.85em' }}>{tr.field_key}</code></TableCell>
                        <TableCell>{tr.option_value || '—'}</TableCell>
                        <TableCell>{LANGUAGES.find(l => l.code === tr.language)?.flag} {tr.language}</TableCell>
                        <TableCell>{tr.translation}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditTransValue(tr.translation);
                              setEditTransDialog({ open: true, row: tr });
                            }}
                            title="Modifier"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau champ de détail</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Saisissez le label en <strong>français</strong> uniquement. Les traductions EN/AR seront générées automatiquement via DeepL.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type d'offre *</InputLabel>
                <Select
                  value={form.offer_type}
                  label="Type d'offre *"
                  onChange={e => setForm(f => ({ ...f, offer_type: e.target.value }))}
                >
                  {OFFER_TYPES.map(ot => <MenuItem key={ot} value={ot}>{ot}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Clé unique *"
                placeholder="nb_chambres"
                value={form.key}
                helperText="minuscules, chiffres, underscores"
                onChange={e => setForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small"
                label="Label en français *"
                placeholder="Nombre de chambres"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type *</InputLabel>
                <Select
                  value={form.field_type}
                  label="Type *"
                  onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}
                >
                  {FIELD_TYPES.map(ft => <MenuItem key={ft} value={ft}>{ft}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Unité"
                placeholder="m², €, ans"
                value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
              />
            </Grid>
            {form.field_type === 'select' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small"
                  label="Options (séparées par virgule)"
                  placeholder="Appartement, Maison, Villa"
                  value={form.options}
                  helperText="Valeurs canoniques FR — seront auto-traduites"
                  onChange={e => setForm(f => ({ ...f, options: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="number"
                label="Ordre d'affichage"
                value={form.display_order}
                onChange={e => setForm(f => ({ ...f, display_order: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_searchable}
                    onChange={e => setForm(f => ({ ...f, is_searchable: e.target.checked }))}
                  />
                }
                label="Utilisé dans recherche"
              />
            </Grid>
            {form.is_searchable && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Widget de recherche</InputLabel>
                  <Select
                    value={form.search_widget}
                    label="Widget"
                    onChange={e => setForm(f => ({ ...f, search_widget: e.target.value }))}
                  >
                    <MenuItem value="">Automatique</MenuItem>
                    {SEARCH_WIDGETS.map(sw => <MenuItem key={sw} value={sw}>{sw}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} disabled={submitting}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreateField}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Créer + auto-traduire
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editTransDialog.open} onClose={() => setEditTransDialog({ open: false, row: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier la traduction</DialogTitle>
        <DialogContent dividers>
          {editTransDialog.row && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Champ:</strong> {editTransDialog.row.field_key}
                {editTransDialog.row.type === 'option' && ` / ${editTransDialog.row.option_value}`}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                <strong>Langue:</strong> {LANGUAGES.find(l => l.code === editTransDialog.row.language)?.flag} {editTransDialog.row.language}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth size="small"
            label="Traduction"
            value={editTransValue}
            onChange={e => setEditTransValue(e.target.value)}
            autoFocus
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTransDialog({ open: false, row: null })}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveTranslation}>Sauvegarder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
